import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { theme } from "../../theme";
import OrderService, { IOrder } from "../../services/OrderService";
import { unwrapPage } from "../../services/apiUtils";
import { orderStatusLabel, orderStatusColor } from "../../services/orderStatusUtils";

const getOrderNo = (row: any) => row?.orderNumber ?? row?.orderId ?? "-";
const getCustomerName = (row: any) =>
  row?.userFName || row?.userName || row?.customerName || (row?.userId != null ? `User #${row.userId}` : "-");
const getTotal = (row: any) => {
  const v = row?.orderTotalPrice ?? row?.totalAmount ?? row?.total;
  return v != null ? `Rs. ${v}` : "-";
};
const getPlacedOn = (row: any) => row?.orderDate ?? row?.createdAt ?? "-";

interface RecentOrdersTableProps {
  variant?: "list" | "grid";
  limit?: number;
}

const RecentOrdersTable: React.FC<RecentOrdersTableProps> = ({ variant = "list", limit = 5 }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    OrderService.filterOrders({ page: 0, size: variant === "list" ? limit : 20 })
      .then((res: any) => {
        const { content } = unwrapPage<IOrder>(res);
        setOrders(content);
      })
      .catch(() => setError("Could not load recent orders."))
      .finally(() => setLoading(false));
  }, [variant, limit]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  if (variant === "list") {
    return (
      <Box height="254px" sx={{ overflowX: "hidden", overflowY: "auto" }}>
        {orders.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No recent orders yet.
          </Typography>
        )}
        {orders.map((order, i) => (
          <Box
            key={`${getOrderNo(order)}-${i}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.primary.main}`}
            gap="8px"
            p="12px 15px"
            sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
            onClick={() => navigate("/orders")}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                color={theme.palette.secondary.main}
                fontWeight="600"
                noWrap
                sx={{ fontSize: "12px", lineHeight: 1.4 }}
              >
                #{getOrderNo(order)}
              </Typography>
              <Typography
                color="text.secondary"
                noWrap
                sx={{ fontSize: "11px", lineHeight: 1.4 }}
              >
                {getCustomerName(order)}
              </Typography>
            </Box>
            <Box color="text.secondary" sx={{ fontSize: "11px", flexShrink: 0 }}>
              {getPlacedOn(order)}
            </Box>
            <Box
              p="3px 8px"
              color={theme.palette.primary.main}
              border={`1px solid ${theme.palette.primary.main}`}
              borderRadius="4px"
              sx={{ fontSize: "11px", flexShrink: 0 }}
            >
              {getTotal(order)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const columns: GridColDef[] = [
    { field: "orderNo", headerName: "Order #", flex: 1 },
    { field: "customer", headerName: "Customer", flex: 1.2 },
    { field: "total", headerName: "Total", flex: 0.8 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={params.value} color={orderStatusColor(params.row.rawStatus) as any} size="small" />
      ),
    },
    { field: "placedOn", headerName: "Placed On", flex: 1 },
  ];

  const rows = orders.map((order, i) => ({
    id: order.orderId ?? i,
    orderNo: getOrderNo(order),
    customer: getCustomerName(order),
    total: getTotal(order),
    status: orderStatusLabel(order.orderStatus),
    rawStatus: order.orderStatus,
    placedOn: getPlacedOn(order),
  }));

  return (
    <Box sx={{ height: 500, mt: 2 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        onRowClick={() => navigate("/orders")}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />
    </Box>
  );
};

export default RecentOrdersTable;
